import moment from "moment";

/**
 * Formats a millisecond duration to HH:mm:ss or mm:ss
 * @param {number} durationMs
 * @returns {string}
 */
export function formatDuration(durationMs) {
  const duration = moment.duration(durationMs);
  if (duration.asHours() >= 1) {
    return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
  }
  return moment.utc(duration.asMilliseconds()).format("mm:ss");
}

/**
 * Returns YYYY-MM-DD HH:mm:ss format for a date string or timestamp.
 * @param {string|number} input
 * @returns {string}
 */
export function getFormattedTime(input) {
  const date = new Date(input);
  if (isNaN(date)) {
    return String(input);
  }
  // Format UTC timestamp to avoid local timezone offsets
  return moment.utc(date).format("YYYY-MM-DD HH:mm:ss");
}

/**
 * Return the base URL (protocol + host + path minus last segment) of current window.location.
 */
export function getBaseUrl() {
  const { origin, protocol, host } = window.location;
  if (origin && origin !== "null") {
    return origin;
  }
  return `${protocol}//${host}`;
}

/**
 * Extracts masterContactId from various shapes of ScreenPopUrlVariables.
 * Supports:
 *  - object with key 'masterContactId' or 'mastercontactid'
 *  - array of objects containing those keys directly
 *  - array of { name/key, value } entries
 *  - string formatted as "key=value|key2=value2" or querystring
 */
export function getMasterContactIdFromScreenPop(screenPopVariables) {
  if (!screenPopVariables) return null;

  const pickFromObject = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    // Direct properties (case-insensitive)
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === "mastercontactid") {
        const v = obj[k];
        if (v != null && String(v).length > 0) return String(v);
      }
    }
    // name/key + value shape
    const name = obj.name || obj.key || obj.Key || obj.Name;
    const value = obj.value || obj.Value || obj.val;
    if (name && String(name).toLowerCase() === "mastercontactid" && value != null) {
      return String(value);
    }
    return null;
  };

  try {
    if (Array.isArray(screenPopVariables)) {
      for (const entry of screenPopVariables) {
        const v = pickFromObject(entry);
        if (v) return v;
      }
      return null;
    }

    if (typeof screenPopVariables === "object") {
      return pickFromObject(screenPopVariables);
    }

    if (typeof screenPopVariables === "string") {
      // Try JSON first
      const s = screenPopVariables.trim();
      if (s.startsWith("{") || s.startsWith("[")) {
        try {
          const parsed = JSON.parse(s);
          return getMasterContactIdFromScreenPop(parsed);
        } catch (_) {
          // fall through to key=value parsing
        }
      }

      // If it's a full URL, try URL parsing for query params
      if (s.includes("://") && s.includes("?")) {
        try {
          const url = new URL(s);
          const v =
            url.searchParams.get("masterContactId") || url.searchParams.get("mastercontactid");
          if (v) return v;
        } catch (_) {
          // ignore and fall through
        }
      }

      // Parse pipe-delimited or querystring format
      const queryish = s.includes("|") ? s.replaceAll("|", "&") : s;
      const params = new URLSearchParams(queryish);
      for (const [k, v] of params.entries()) {
        if (k.toLowerCase() === "mastercontactid" && v) return v;
      }
    }
  } catch (_) {
    // Swallow parse errors and return null for safety
  }

  return null;
}
